import { CustomersFilter } from "../pages/customers/models";
import { CustomerStatus } from "../pages/models";

const key = "aml.customerFilters";
let owner: string | null = null;

/** Creates an independent default search for active customers. */
export function defaultFilters(): CustomersFilter {
    return { name: null, vatCode: null, managementCode: null, worker: null, office: null, status: null, deleted: false };
}

/** Selects the authenticated owner before routes mount, clearing another user's snapshot. */
export function setFilterOwner(subject: string | null): void {
    owner = subject;
    try {
        const saved = JSON.parse(sessionStorage.getItem(key) || "null");
        if (!subject || (saved && saved.owner !== subject)) sessionStorage.removeItem(key);
    } catch { try { sessionStorage.removeItem(key); } catch {} }
}

/** Invalidates active page writers before logout redirects begin. */
export function clearFilterSession(): void {
    setFilterOwner(null);
}

/** Captures the owner so an outgoing page cannot write into a later session. */
export function filterOwner(): string | null { return owner; }

/** Reads a validated snapshot, falling back safely when storage is unavailable or malformed. */
export function loadFilters(): CustomersFilter {
    try {
        const saved = JSON.parse(sessionStorage.getItem(key) || "null");
        const f = saved?.filters;
        if (!owner || saved?.owner !== owner || !f || typeof f.deleted !== "boolean") return defaultFilters();
        if (!["name", "vatCode", "managementCode", "worker"].every(k => f[k] === null || typeof f[k] === "string")) return defaultFilters();
        if (f.status !== null && !Object.values(CustomerStatus).includes(f.status)) return defaultFilters();
        return { name: f.name, vatCode: f.vatCode, managementCode: f.managementCode, worker: f.worker, status: f.status, deleted: f.deleted, office: null };
    } catch { return defaultFilters(); }
}

/** Persists only changed exit snapshots belonging to the still-active authenticated session. */
export function saveFilters(filters: CustomersFilter, pageOwner: string | null): void {
    if (!pageOwner || pageOwner !== owner) return;
    try {
        const defaults = defaultFilters();
        if ((Object.keys(defaults) as Array<keyof CustomersFilter>).every(field => filters[field] === defaults[field])) {
            if (sessionStorage.getItem(key) !== null) sessionStorage.removeItem(key);
            return;
        }
        const value = JSON.stringify({ owner, filters });
        if (sessionStorage.getItem(key) !== value) sessionStorage.setItem(key, value);
    } catch {}
}
